import React, { useCallback } from 'react';
import { NextPage } from 'next';
import {
  Button, ButtonColor, ButtonSize, ButtonVariant, Image, TypographyName,
} from '@doveit/lego';
import dynamic from 'next/dynamic';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import PageHead from '../client/components/page-head/PageHead';
import PageSection from '../client/components/page-section/PageSection';
import Hero from '../client/templates/common/hero/Hero';
import { requireFromStaticAsset, requireResourcesFromStaticAssets } from '../client/utils/assetsUtils';
import {
  City, CTA, HeroSection, TextAlign,
} from '../client/types';
import scrollToRef from '../client/utils/scroll-to-ref/scrollToRef';
import { ReviewTarget } from '../domain/graphcms';
import HowItWorksSection, { HowItWorksSectionProps } from '../client/templates/common/how-it-works-section/HowItWorksSection';
import LogoList from '../client/components/logo-list/LogoList';
import { LogosSectionProps } from '../client/templates/common/logos-section/LogosSection';
import { sendEventAndRedirect } from '../client/utils/tracking';
import { buildURLObject, BUYER_CONSULTANCY_THANK_YOU_AS, THANK_YOU_URL } from '../client/utils/links';
import PackageCard, { PackageCardProps } from '../client/components/package-card/PackageCard';
import CitiesHList from '../client/components/cities-hlist/CitiesHList';
import { getStructuredData } from '../data/structured';
import * as styles from '../client/templates/buyer-consultancy/BuyerConsultancy.style';
import Stripe, { StripeAlignemnt, StripeDirection } from '../client/components/stripe/Stripe';
import Card from '../client/components/card/Card';
import { getHighlightedCities } from '../services/cities/cities';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import CreateConsultancy from '../client/containers/create-consultancy/CreateConsultancy';
import { ConsultancyType } from '../services/dtos/consultancy';
import { getPartnersData } from '../services/partners/getPartnersData';
import getTrafficSource from '../client/utils/traffic-source/getTrafficSource';
import useRefCallback from '../client/hooks/useRefCallback/useRefCallback';
import { ThankYouPageVariant } from '../services/thank-you/getThankYouData';

const ListReviews = dynamic(() => import('../client/containers/list-reviews/ListReviews'), {
  ssr: false,
});

export interface BuyerConsultancyProps {
  SEO: {
    title: string,
    description: string,
  },
  hero: HeroSection,
  serviceDescription: {
    title: string,
    subtitle: string,
    description: string,
    cta?: CTA,
  },
  whyChooseSection: {
    title: string,
    description: string,
  },
  buyFromAgency: {
    title: string,
    description: string,
    picture: string,
    cta?: CTA,
  },
  buyFromPrivate: {
    title: string,
    description: string,
    picture: string,
    cta?: CTA,
  },
  servicesSection: {
    title: string,
    picture: {
      name: string,
      alt: string,
    },
    items: {
      title: string,
      description: string,
    }[],
  },
  priceSection: {
    title: string,
    packages: PackageCardProps[],
  },
  citiesSection: {
    title: string,
    subtitle: string,
    cities: City[],
    cta?: CTA,
  },
  howItWorksSection: HowItWorksSectionProps & { description?: string },
  press: LogosSectionProps & { cta?: CTA },
}

export const BuyerConsultancy: NextPage<BuyerConsultancyProps> = ({
  SEO,
  hero,
  serviceDescription,
  whyChooseSection,
  buyFromAgency,
  buyFromPrivate,
  servicesSection,
  priceSection,
  citiesSection,
  howItWorksSection,
  press,
}) => {
  const { current, ref: formRef } = useRefCallback();

  const onSuccess = useCallback(() => {
    sendEventAndRedirect(
      'buyer_consultancy_submit',
      {
        action: 'submit',
        category: 'buyer-consultancy',
        label: 'hero',
      },
      {
        urlObject: buildURLObject(
          THANK_YOU_URL,
          BUYER_CONSULTANCY_THANK_YOU_AS,
          { variant: ThankYouPageVariant.BUYER_CONSULTANCY },
        ),
      },
    );
  }, []);

  return (
    <AppLayout>
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData({
          '@type': 'HowTo',
          name: howItWorksSection.title,
          description: howItWorksSection.description,
          step: howItWorksSection.steps?.map((step, i) => ({
            '@type': 'HowToStep',
            name: step.title,
            text: step.description,
            position: i + 1,
            image: requireFromStaticAsset(howItWorksSection.picture.name),
          })) ?? [],
        })}
      />
      <PageLayout>
        <PageLayout.Header>
          <Hero
            title={hero.title}
            backgroundImage={requireResourcesFromStaticAssets(hero.backgroundImage)}
            description={hero.description}
            secondarySlotTitle={hero.formTitle}
            secondarySlot={(
              <div ref={formRef}>
                <CreateConsultancy
                  dataTracking="buyer-consultancy-form"
                  onSuccess={onSuccess}
                  consultancyType={ConsultancyType.BUYER}
                />
              </div>
            )}
          />
        </PageLayout.Header>
        <PageLayout.Content>
          <PageLayout.Main>
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  textAlign={TextAlign.CENTER}
                  content={whyChooseSection.title}
                />
                <PageSection.Block>
                  <PageSection.Description
                    textAlign={TextAlign.CENTER}
                    text={whyChooseSection.description}
                  />
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>
            <PageSection>
              <PageSection.Wrapper>
                <Stripe
                  title={buyFromPrivate.title}
                  description={buyFromPrivate.description}
                  alignContent={StripeAlignemnt.CENTER}
                  picture={{
                    src: requireFromStaticAsset(buyFromPrivate.picture),
                    alt: `Icona ${buyFromPrivate.title}`,
                  }}
                >
                  {buyFromPrivate.cta && (
                    <Button
                      {...buyFromPrivate.cta}
                      withoutSpacing
                      color={ButtonColor.PRIMARY}
                      variant={ButtonVariant.LIGHT}
                      size={ButtonSize.SMALL}
                      onClick={scrollToRef(current)}
                    />
                  )}
                </Stripe>
              </PageSection.Wrapper>
            </PageSection>
            <PageSection>
              <PageSection.Wrapper>
                <Stripe
                  title={buyFromAgency.title}
                  description={buyFromAgency.description}
                  alignContent={StripeAlignemnt.CENTER}
                  direction={StripeDirection.RIGHT_TO_LEFT}
                  picture={{
                    src: requireFromStaticAsset(buyFromAgency.picture),
                    alt: `Icona ${buyFromAgency.title}`,
                  }}
                >
                  {buyFromAgency.cta && (
                    <Button
                      {...buyFromAgency.cta}
                      withoutSpacing
                      color={ButtonColor.PRIMARY}
                      variant={ButtonVariant.LIGHT}
                      size={ButtonSize.SMALL}
                      onClick={scrollToRef(current)}
                    />
                  )}
                </Stripe>
              </PageSection.Wrapper>
            </PageSection>
            <HowItWorksSection
              title={howItWorksSection.title}
              picture={howItWorksSection.picture}
              steps={howItWorksSection.steps}
            />
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  content={servicesSection.title}
                />
                <PageSection.Block>
                  <div css={styles.services}>
                    <div css={styles.servicesBlock}>
                      <div css={styles.servicesList}>
                        {servicesSection.items.map((service) => (
                          <div
                            key={service.title}
                            css={styles.serviceItem}
                          >
                            <Card>
                              <Card.Title>
                                {service.title}
                              </Card.Title>
                              <Card.Text>
                                {service.description}
                              </Card.Text>
                            </Card>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div css={styles.servicesImage}>
                      <Image
                        src={requireFromStaticAsset(servicesSection.picture.name)}
                        alt={servicesSection.picture.alt}
                      />
                    </div>
                  </div>
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  content={priceSection.title}
                  textAlign={TextAlign.CENTER}
                />
                <PageSection.Block>
                  <div css={styles.packageList}>
                    {priceSection.packages.map((item) => (
                      <div
                        key={item.title}
                        css={styles.packageItem}
                      >
                        <PackageCard
                          {...item}
                          cta={{
                            ...item.cta,
                            onClick: scrollToRef(current),
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title textAlign={TextAlign.CENTER}>
                  {citiesSection.title}
                </PageSection.Title>
                {citiesSection.subtitle && (
                  <PageSection.Description
                    textAlign={TextAlign.CENTER}
                    size={TypographyName.H4}
                    color="neutrals.dark"
                    text={citiesSection.subtitle}
                  />
                )}
              </PageSection.Wrapper>
              <PageSection.Wrapper withoutPad>
                <PageSection.Block>
                  <CitiesHList cities={citiesSection.cities} />
                </PageSection.Block>
              </PageSection.Wrapper>
              {citiesSection.cta && (
                <PageSection.Wrapper>
                  <PageSection.Actions textAlign={TextAlign.CENTER}>
                    <Button
                      {...citiesSection.cta}
                      color={ButtonColor.SECONDARY}
                      onClick={scrollToRef(current)}
                    />
                  </PageSection.Actions>
                </PageSection.Wrapper>
              )}
            </PageSection>
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  textAlign={TextAlign.CENTER}
                  content={serviceDescription.title}
                />
                <PageSection.Description
                  textAlign={TextAlign.CENTER}
                  text={serviceDescription.subtitle}
                  size={TypographyName.H4}
                  color="neutrals.dark"
                />
                <PageSection.Block>
                  <PageSection.Description
                    textAlign={TextAlign.CENTER}
                    text={serviceDescription.description}
                  />
                </PageSection.Block>
                {serviceDescription.cta && (
                  <PageSection.Actions textAlign={TextAlign.CENTER}>
                    <Button
                      {...serviceDescription.cta}
                      color={ButtonColor.SECONDARY}
                      onClick={scrollToRef(current)}
                    />
                  </PageSection.Actions>
                )}
              </PageSection.Wrapper>
            </PageSection>
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  textAlign={TextAlign.CENTER}
                  content="Le recensioni dei nostri clienti su Trustpilot"
                />
                <PageSection.Description
                  textAlign={TextAlign.CENTER}
                  text="Il 98% dei nostri clienti ha giudicato il servizio <strong>ECCELLENTE</strong>"
                  size={TypographyName.H4}
                  color="neutrals.dark"
                />
              </PageSection.Wrapper>
              <PageSection.Wrapper withoutPad>
                <PageSection.Block>
                  <ListReviews
                    target={[ReviewTarget.COMPRATORE]}
                    showLink
                  />
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  textAlign={TextAlign.CENTER}
                  content={press.title}
                />
                <PageSection.Block>
                  <LogoList
                    items={press.items
                      .map((item) => ({
                        ...item,
                        picture: requireFromStaticAsset(item.picture),
                      }))}
                    inlineItems={6}
                  />
                </PageSection.Block>
                {press.cta && (
                  <PageSection.Actions textAlign={TextAlign.CENTER}>
                    <Button
                      {...press.cta}
                      color={ButtonColor.SECONDARY}
                      onClick={scrollToRef(current)}
                    />
                  </PageSection.Actions>
                )}
              </PageSection.Wrapper>
            </PageSection>
          </PageLayout.Main>
        </PageLayout.Content>
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<BuyerConsultancyProps>(async ({ query }) => {
  const [pageData, partnerData, cities, press] = await Promise.all([
    importDynamicData('pages/buyer-consultancy.yml'),
    getPartnersData(getTrafficSource(query), 'buyer-consultancy.yml'),
    getHighlightedCities(),
    importDynamicData('press.yml'),
  ]);

  const mergedData = { ...pageData, ...partnerData };

  return {
    props: {
      ...mergedData,
      citiesSection: {
        cities,
        ...mergedData.citiesSection,
      },
      press: {
        ...mergedData.press,
        items: press.items,
      },
    },
  };
});

export default withError(BuyerConsultancy);
